import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { isMultiMemberPackageKind } from "@/modules/private-lessons/domain/lib/package-kind";
import { useCompletePrivateLessonBooking } from "@/modules/private-lessons/presentation/api/queries";
import { CompleteRoomBookingDialog } from "@/modules/room-bookings/presentation/ui/CompleteRoomBookingDialog";
import { formatDate } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import {
  addMonths,
  format,
  getDay,
  isSameDay,
  isSameMonth,
  startOfMonth,
  subMonths,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import type { CheckInListItem } from "../api/client";
import {
  useCheckInListQuery,
  useCompleteClassSessionMutation,
} from "../api/queries";
import { CheckInHubTable } from "../ui/CheckInHubTable";
import { CheckInModal } from "../ui/CheckInModal";

const WEEKDAYS = ["ອາ", "ຈ", "ອ", "ພ", "ພຫ", "ສຸ", "ສ"];

export function CheckInHubPage() {
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [modalItem, setModalItem] = useState<CheckInListItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [completeRoomBookingId, setCompleteRoomBookingId] = useState<
    string | null
  >(null);

  const dateStr = format(selectedDate, "yyyy-MM-dd");
  const { data, isLoading, refetch } = useCheckInListQuery(dateStr);
  const completeSession = useCompleteClassSessionMutation();
  const completePrivateBooking = useCompletePrivateLessonBooking();

  const items = data?.items ?? [];
  const totalSessions = data?.totalSessions ?? 0;
  const checkedInCount = data?.checkedInCount ?? 0;
  const progressPercent =
    totalSessions > 0
      ? Math.round((checkedInCount / totalSessions) * 1000) / 10
      : 0;

  const monthStart = startOfMonth(selectedDate);
  const calendarDays = useMemo(() => {
    const daysInMonth = new Date(
      monthStart.getFullYear(),
      monthStart.getMonth() + 1,
      0,
    ).getDate();
    const firstDow = getDay(monthStart); // 0 = อาทิตย์, 1 = จันทร์, ... 6 = เสาร์ (ให้ตรงกับ WEEKDAYS: ອາ, ຈ, ອ, ພ, ພຫ, ສຸ, ສ)
    const pad = firstDow;
    const cells: (number | null)[] = [];
    for (let i = 0; i < pad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [monthStart]);

  const handlePrevDay = useCallback(() => {
    setSelectedDate((d) => {
      const next = new Date(d);
      next.setDate(next.getDate() - 1);
      return next;
    });
  }, []);
  const handleNextDay = useCallback(() => {
    setSelectedDate((d) => {
      const next = new Date(d);
      next.setDate(next.getDate() + 1);
      return next;
    });
  }, []);

  const handleCheckIn = useCallback((item: CheckInListItem) => {
    setModalItem(item);
    setModalOpen(true);
  }, []);

  const handleCompleteSession = useCallback(
    async (item: CheckInListItem) => {
      if (item.type === "general_class") {
        const ok = await confirm({
          title: "ສຳເລັດຊົ່ວໂມງຮຽນ",
          description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະສຳເລັດ ຊົ່ວໂມງຮຽນ ນີ້?",
          actionText: "ສຳເລັດ",
        });
        if (ok) completeSession.mutate(item.id);
        return;
      }
      if (item.type === "room_booking") {
        setCompleteRoomBookingId(item.id);
        return;
      }
      if (
        item.type === "private_lesson" &&
        isMultiMemberPackageKind(item.packageKind)
      ) {
        const ok = await confirm({
          title: "ສຳເລັດການຈອງກຸ່ມ",
          description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະອັບເດດການຈອງນີ້ເປັນສຳເລັດ?",
          actionText: "ສຳເລັດ",
        });
        if (ok) completePrivateBooking.mutate(item.id);
      }
    },
    [completeSession, completePrivateBooking],
  );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ກວດຊື່" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="">
            <h1 className="font-bold text-2xl">{formatDate(selectedDate)}</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຈັດການການມາຮອດຂອງນັກຮຽນ ແລະ ຢືນຢັນຊົ່ວໂມງຮຽນທີ່ກຳນົດໄວ້
            </p>
          </div>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2">
              <CalendarIcon className="size-4 text-muted-foreground" />
              <span className="font-medium text-sm">
                {formatDate(selectedDate)}
              </span>
              <button
                type="button"
                className="rounded p-1 hover:bg-muted"
                onClick={handlePrevDay}
                aria-label="ວັນກ່ອນໜ້າ"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="rounded p-1 hover:bg-muted"
                onClick={handleNextDay}
                aria-label="ວັນຖັດໄປ"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Left panel: Calendar + Check-in Progress */}
          <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-64">
            <div className="rounded-xl border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-medium">
                  {format(monthStart, "MMMM yyyy", { locale: enUS })}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="rounded p-1 hover:bg-muted"
                    onClick={() => setSelectedDate(subMonths(selectedDate, 1))}
                    aria-label="ເດືອນກ່ອນໜ້າ"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 hover:bg-muted"
                    onClick={() => setSelectedDate(addMonths(selectedDate, 1))}
                    aria-label="ເດືອນຖັດໄປ"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-0.5 text-center text-muted-foreground text-xs">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="py-1">
                    {d}
                  </div>
                ))}
                {calendarDays.map((d, i) => {
                  if (d === null) return <div key={`empty-${i}`} />;
                  const cellDate = new Date(
                    monthStart.getFullYear(),
                    monthStart.getMonth(),
                    d,
                  );
                  const isSelected = isSameDay(cellDate, selectedDate);
                  const isCurrentMonth = isSameMonth(cellDate, monthStart);
                  return (
                    <button
                      key={d}
                      type="button"
                      className={`rounded p-1.5 text-sm ${isSelected
                          ? "bg-primary text-primary-foreground"
                          : isCurrentMonth
                            ? "hover:bg-muted"
                            : "text-muted-foreground/50"
                        }`}
                      onClick={() => setSelectedDate(cellDate)}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border bg-card p-4">
              <h3 className="mb-2 font-medium text-sm">ຄວາມຄືບໜ້າກວດຊື່</h3>
              <div className="font-bold text-2xl">
                {checkedInCount}/{totalSessions}
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="mt-1 text-muted-foreground text-xs">
                {progressPercent}%
              </div>
            </div>
          </aside>

          {/* Right: Date nav + Table */}
          <div className="min-w-0 flex-1">
            <div className="rounded-xl border bg-card">
              <CheckInHubTable
                data={items}
                isLoading={isLoading}
                onCheckIn={handleCheckIn}
                onCompleteSession={handleCompleteSession}
                completingSessionId={
                  completeSession.isPending
                    ? completeSession.variables
                    : completePrivateBooking.isPending
                      ? completePrivateBooking.variables
                      : completeRoomBookingId
                }
              />
            </div>
          </div>
        </div>
      </Main>

      <CheckInModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        item={modalItem}
        onSuccess={() => refetch()}
      />

      <CompleteRoomBookingDialog
        bookingId={completeRoomBookingId}
        open={!!completeRoomBookingId}
        onOpenChange={(open) => {
          if (!open) setCompleteRoomBookingId(null);
        }}
        onSuccess={() => {
          toast.success("ອັບເດດການຈອງຫ້ອງເປັນສຳເລັດແລ້ວ");
          void refetch();
        }}
      />
    </>
  );
}
